import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUrlPagination } from '@/hooks/useUrlPagination';
import { Pagination } from '@/components/Pagination';
import { useNavigate, useParams, useLoaderData } from 'react-router-dom';
import useSWR from 'swr';
import { toast } from 'sonner';
import { useDebounceValue } from 'usehooks-ts';
import { Users, CheckCircle, UserPlus, AlertTriangle } from 'lucide-react';
import API from '@/api/api';
import {
    ClassLoaderData,
    EnrollmentStatus,
    SelectedClassStatus,
    ServerResponseMany,
    User,
    FilterResidentNames,
    ConflictDetail
} from '@/types';
import { PageHeader, SearchInput } from '@/components/shared';
import { FormModal } from '@/components/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import {
    classEnrollmentsSchema,
    ClassEnrollmentsInput
} from '@/lib/validation';

function StatItem({
    icon,
    label,
    value
}: {
    icon: React.ReactNode;
    label: string;
    value: number;
}) {
    return (
        <div className="flex items-center gap-3">
            <div className="rounded-lg bg-muted p-2">{icon}</div>
            <div>
                <p className="text-sm text-muted-foreground">{label}</p>
                <p className="text-lg font-bold text-foreground">{value}</p>
            </div>
        </div>
    );
}

export default function AddClassEnrollments() {
    const navigate = useNavigate();
    const { class_id } = useParams<{ class_id: string }>();
    const loaderData = useLoaderData() as ClassLoaderData;
    const classInfo = loaderData?.class;

    const form = useForm<ClassEnrollmentsInput>({
        resolver: zodResolver(classEnrollmentsSchema),
        defaultValues: { user_ids: [] }
    });
    const selectedUsers = form.watch('user_ids');
    const setSelectedUsers = (
        next: number[] | ((prev: number[]) => number[])
    ) => {
        const value =
            typeof next === 'function'
                ? next(form.getValues('user_ids'))
                : next;
        form.setValue('user_ids', value);
    };

    const [errorMessage, setErrorMessage] = useState('');
    const { page, perPage, setPage, setPerPage } = useUrlPagination();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchQuery] = useDebounceValue(searchTerm, 500);
    const [sortQuery, setSortQuery] = useState(
        FilterResidentNames['Resident Name (A-Z)']
    );

    const [conflicts, setConflicts] = useState<ConflictDetail[]>([]);
    const [showConflictDialog, setShowConflictDialog] = useState(false);

    const encodedSearch = encodeURIComponent(searchQuery);

    const { data, isLoading } = useSWR<ServerResponseMany<User>>(
        `/api/users?search=${encodedSearch}&page=${page}&per_page=${perPage}&order_by=${sortQuery}&role=student&class_id=${class_id}&include=only_unenrolled`
    );

    const users = data?.data ?? [];
    const meta = data?.meta;
    const enrolledCount =
        classInfo?.enrollments?.filter(
            (e) => e.enrollment_status === EnrollmentStatus.Enrolled
        ).length ?? 0;

    const remainingCapacity =
        (classInfo?.capacity ?? 0) - enrolledCount - selectedUsers.length;

    useEffect(() => {
        if (remainingCapacity < 0) {
            setErrorMessage('Class is full');
            setSelectedUsers((prev) =>
                prev.slice(0, prev.length + remainingCapacity)
            );
        } else if (remainingCapacity > 0 && errorMessage === 'Class is full') {
            setErrorMessage('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remainingCapacity, errorMessage]);

    function handleToggleRow(userId: number) {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    }

    function handleSelectAll() {
        setSelectedUsers(
            selectedUsers.length === users.length ? [] : users.map((u) => u.id)
        );
    }

    const allSelected =
        users.length > 0 && users.every((u) => selectedUsers.includes(u.id));

    async function submitEnrollment(confirm = false) {
        setErrorMessage('');
        interface EnrollmentResponse {
            conflicts?: ConflictDetail[];
        }
        const resp = await API.post<
            EnrollmentResponse,
            { user_ids: number[]; confirm: boolean }
        >(`program-classes/${class_id}/enrollments`, {
            user_ids: form.getValues('user_ids'),
            confirm
        });

        if (!resp.success) {
            const responseData = resp.data as EnrollmentResponse;
            if (resp.status === 409 && responseData?.conflicts) {
                setConflicts(responseData.conflicts);
                setShowConflictDialog(true);
            } else {
                setErrorMessage(
                    resp.message || 'Failed to enroll users. Please try again.'
                );
            }
            return;
        }

        toast.success('Residents enrolled successfully');
        form.setValue('user_ids', []);
        navigate(`/program-classes/${class_id}/enrollments`);
    }

    async function handleSubmit() {
        if (
            classInfo?.status === SelectedClassStatus.Completed ||
            classInfo?.status === SelectedClassStatus.Cancelled
        ) {
            setErrorMessage(
                'Cannot add users to a class that is completed or cancelled.'
            );
            return;
        }
        await submitEnrollment(false);
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Add Residents"
                subtitle={`Add residents to ${classInfo?.name ?? 'class'}`}
            />

            <Card className="bg-card">
                <CardContent className="p-4">
                    <h2 className="text-lg font-semibold text-foreground mb-3">
                        {classInfo?.name}
                    </h2>
                    <div className="grid grid-cols-3 gap-6">
                        <StatItem
                            icon={<Users className="size-5 text-brand" />}
                            label="Current Enrollment"
                            value={enrolledCount}
                        />
                        <StatItem
                            icon={<CheckCircle className="size-5 text-brand" />}
                            label="Maximum Capacity"
                            value={classInfo?.capacity ?? 0}
                        />
                        <StatItem
                            icon={<UserPlus className="size-5 text-brand" />}
                            label="Available Spots"
                            value={remainingCapacity}
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="flex items-center gap-3">
                <SearchInput
                    value={searchTerm}
                    onChange={(value) => {
                        setSearchTerm(value);
                        setPage(1);
                    }}
                    placeholder="Search residents..."
                    className="w-64"
                />
                <Select
                    value={sortQuery}
                    onValueChange={(v) =>
                        setSortQuery(v as FilterResidentNames)
                    }
                >
                    <SelectTrigger className="w-44">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="name_last asc">
                            Name (A-Z)
                        </SelectItem>
                        <SelectItem value="name_last desc">
                            Name (Z-A)
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <Form {...form}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void form.handleSubmit(() => void handleSubmit())(e);
                    }}
                >
                    <div className="bg-card rounded-lg border border-border">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="w-10">
                                        <Checkbox
                                            checked={allSelected}
                                            onCheckedChange={handleSelectAll}
                                        />
                                    </TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Resident ID</TableHead>
                                    <TableHead>Username</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            className="text-center py-8"
                                        >
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            className="text-center py-8 text-muted-foreground"
                                        >
                                            No eligible residents available at
                                            this facility.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user) => {
                                        const isSelected =
                                            selectedUsers.includes(user.id);
                                        return (
                                            <TableRow
                                                key={user.id}
                                                className={`cursor-pointer ${isSelected ? 'bg-muted/30' : ''}`}
                                                onClick={() =>
                                                    handleToggleRow(user.id)
                                                }
                                            >
                                                <TableCell
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() =>
                                                            handleToggleRow(
                                                                user.id
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium text-foreground">
                                                    {user.name_last},{' '}
                                                    {user.name_first}
                                                </TableCell>
                                                <TableCell>
                                                    {user.doc_id}
                                                </TableCell>
                                                <TableCell>
                                                    {user.username}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>

                        {(meta?.total ?? 0) > 0 && (
                            <Pagination
                                currentPage={page}
                                totalItems={meta?.total ?? 0}
                                itemsPerPage={perPage}
                                onPageChange={setPage}
                                onItemsPerPageChange={setPerPage}
                                itemLabel="residents"
                            />
                        )}
                    </div>

                    <FormField
                        control={form.control}
                        name="user_ids"
                        render={() => (
                            <FormItem>
                                <FormMessage className="mt-2" />
                            </FormItem>
                        )}
                    />

                    {errorMessage && (
                        <div className="text-sm text-destructive mt-2">
                            {errorMessage}
                        </div>
                    )}

                    <div className="flex items-center justify-between py-4 border-t mt-4 sticky bottom-0 bg-card">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>
                                {selectedUsers.length} resident
                                {selectedUsers.length === 1 ? '' : 's'} selected
                            </span>
                            <span>
                                {remainingCapacity} spot
                                {remainingCapacity === 1 ? '' : 's'} remaining
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    navigate(
                                        `/program-classes/${class_id}/enrollments`
                                    )
                                }
                                className="border-gray-300"
                            >
                                Cancel
                            </Button>
                            <Button type="submit" className="btn-gold-thin">
                                Enroll Residents
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <FormModal
                open={showConflictDialog}
                onOpenChange={setShowConflictDialog}
                title={
                    <span className="flex items-center gap-2">
                        <AlertTriangle className="size-5 text-amber-600" />
                        Scheduling Conflicts
                    </span>
                }
                description={`${conflicts.length} resident${conflicts.length === 1 ? '' : 's'} ha${conflicts.length === 1 ? 's' : 've'} scheduling conflicts with existing classes.`}
                className="max-w-lg"
                preventOutsideClose
            >
                <div className="max-h-60 overflow-y-auto space-y-2">
                    {conflicts.map((c) => (
                        <div
                            key={`${c.user_id}-${c.conflicting_class}`}
                            className="rounded-lg border border-amber-200 bg-amber-50/50 p-3"
                        >
                            <div className="font-medium text-sm text-brand-dark">
                                {c.user_name}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                Conflicts with{' '}
                                <span className="font-medium">
                                    {c.conflicting_class}
                                </span>
                            </div>
                            {(c.conflict_start || c.conflict_end) && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                    {c.conflict_start} - {c.conflict_end}
                                </div>
                            )}
                            {c.conflict_days?.length > 0 && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                    on {c.conflict_days.join(', ')}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => setShowConflictDialog(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => {
                            setShowConflictDialog(false);
                            void submitEnrollment(true);
                        }}
                    >
                        Enroll Anyway
                    </Button>
                </div>
            </FormModal>
        </div>
    );
}
